import { useEffect, useMemo, useState } from 'react';
import { Icon, IconButton, LinearProgress, TablePagination, Paper, Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TableRow, Button, Typography, AlertColor } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { DadosVariaveisService } from 'shared/services/dadosVariaveis/dadosVariaveisService';
import { IListagemDadosVariaveis } from 'shared/types';
import { FerramentasDaListagem, IListaFiltros, AlertaSnackBar } from 'shared/components';
import { LayoutBaseDePagina } from 'shared/layouts';
import { useHasPermissionAcao } from 'shared/hooks';

export const ListagemDeDadosVariaveis: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const navigate = useNavigate();
  const { t } = useTranslation();

  const [stateSnack, setStateSnack] = useState({
    open: false,
    severity: 'success',
    message: '',
  });

  const hasPermitionCreate = useHasPermissionAcao('dadosvariaveis','CRE');
  const hasPermitionRead = useHasPermissionAcao('dadosvariaveis','REA');
  
  const [rows, setRows] = useState<IListagemDadosVariaveis[]>([]);

  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  const [eventoPesquisar, setEventoPesquisar] = useState(false);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const buscaTag = useMemo(() => {
    return searchParams.get('tagDadosVariaveis') || '';
  }, [searchParams]);

  const buscaNome = useMemo(() => {
    return searchParams.get('nomeDadosVariaveis') || '';
  }, [searchParams]);
  
  const buscaOrderBy = useMemo(() => {
    return searchParams.get('orderBy') || '';
  }, [searchParams]);

  const offSet = useMemo(() => {
    return searchParams.get('offset') || '0';
  }, [searchParams]);
  
  const limit = useMemo(() => {
    return searchParams.get('limit') || '10';
  }, [searchParams]);

  const listaFiltros: IListaFiltros[] = [
    {
      tipo: 'TextField',
      mapValue: [],
      label: t('pages.dadosVariaveis.tagDadosVariaveis'),
      textoBusca: buscaTag,
      handleTextoBusca: texto => { searchParams.set('tagDadosVariaveis', texto.toString()); setSearchParams(searchParams, { replace: true }); }
    },
    {
      tipo: 'TextField',
      mapValue: [],
      label: t('pages.dadosVariaveis.nomeDadosVariaveis'),
      textoBusca: buscaNome,
      handleTextoBusca: texto => { searchParams.set('nomeDadosVariaveis', texto.toString()); setSearchParams(searchParams, { replace: true }); }
    },
    
  ];

  useEffect(() => {
    setIsLoading(true);
    DadosVariaveisService.getAll(searchParams.toString())
      .then((result) => {
        setIsLoading(false);

        if (result instanceof Error) {
          setStateSnack({open:true, severity:'error',message:`Ocorreu o seguinte erro: ${result.message}`});
        } else {
          setTotalCount(result.count);
          setRows(result.rows);
          setRowsPerPage(Number.parseInt(limit.toString()));
          setPage(Number.parseInt(offSet.toString())/Number.parseInt(limit.toString()));
        }
      });

  }, [eventoPesquisar]);

  const handleLimpar = () => {
    // eslint-disable-next-line prefer-const
    let parametros: string[] = [];
    searchParams.forEach(function (value, key) {
      parametros.push(key);
    });
    parametros.map((item) => (
      searchParams.delete(item)
    ));
    setPage(0);
    searchParams.set('offset', '0'); setSearchParams(searchParams, { replace: true });
    setEventoPesquisar(!eventoPesquisar);
  };

  const handlePesquisar = () => {
    setPage(0);
    searchParams.set('offset', '0'); setSearchParams(searchParams, { replace: true });
    setEventoPesquisar(!eventoPesquisar);
  };

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setPage(newPage);
    searchParams.set('offset', (newPage * rowsPerPage).toString()); setSearchParams(searchParams, { replace: true });
    setEventoPesquisar(!eventoPesquisar);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    searchParams.set('limit', event.target.value.toString()); 
    searchParams.set('offset', '0');
    setSearchParams(searchParams, { replace: true });
    setEventoPesquisar(!eventoPesquisar);
  };

  const handleOrdenacao = (campo:string) => {
    searchParams.set('orderBy', campo); 
    setSearchParams(searchParams, { replace: true });
    setEventoPesquisar(!eventoPesquisar);
  };

  const handleCloseSnack = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }

    setStateSnack({...stateSnack, open:false});
  };
  

  return (
    <>
      <LayoutBaseDePagina
        titulo={t('pages.dadosVariaveis.dadosVariaveis')}
        botaoLog={
          <Button
            color='info'
            disableElevation
            variant='contained'
            size='small'
            onClick={() => navigate('/dadosvariaveis/listagem/logs')}
            startIcon={<Icon>crop</Icon>}
          >
            <Typography variant='button' whiteSpace="nowrap" textOverflow="ellipsis" overflow="hidden">
              {t('components.ferramentasDaListagem.logs')}
            </Typography>
          </Button>
        }
        drawerDeFiltros={
          <FerramentasDaListagem
            listaFiltros={listaFiltros}
            handlePesquisar={() => handlePesquisar()}
            handleLimpar={() => handleLimpar()}
          />
        }
      >
        <TableContainer component={Paper} variant="outlined" sx={{ m: 1, width: 'auto' }}>
          <Table stickyHeader >
            <TableHead>
              <TableRow>
                <TableCell width={60}>{t('global.acoes')}</TableCell>
                <TableCell onDoubleClick={() => handleOrdenacao(buscaOrderBy === 'tagDadosVariaveis_ASC'?'tagDadosVariaveis_DESC':'tagDadosVariaveis_ASC')}>
                  {t('pages.dadosVariaveis.tagDadosVariaveis')}
                  <Icon fontSize="inherit" sx={{verticalAlign:'middle'}}>{buscaOrderBy === 'tagDadosVariaveis_ASC'?'north':buscaOrderBy === 'tagDadosVariaveis_DESC'?'south':''}</Icon>
                </TableCell>
                <TableCell onDoubleClick={() => handleOrdenacao(buscaOrderBy === 'nomeDadosVariaveis_ASC'?'nomeDadosVariaveis_DESC':'nomeDadosVariaveis_ASC')}>
                  {t('pages.dadosVariaveis.nomeDadosVariaveis')}
                  <Icon fontSize="inherit" sx={{verticalAlign:'middle'}}>{buscaOrderBy === 'nomeDadosVariaveis_ASC'?'north':buscaOrderBy === 'nomeDadosVariaveis_DESC'?'south':''}</Icon>
                </TableCell>
                <TableCell>{t('pages.dadosVariaveis.tamMaxDadosVariaveis')}</TableCell>                
                <TableCell>{t('pages.dadosVariaveis.descDadosVariaveis')}</TableCell>                
              </TableRow>
            </TableHead>
            {!isLoading && (<TableBody>
              {rows.map((row) => (
                <TableRow key={row.TagDadosVariaveis}>
                  <TableCell>
                    <IconButton size="small" disabled={!hasPermitionRead} onClick={() => navigate(`/dadosvariaveis/detalhe/${row.TagDadosVariaveis}`)}>
                      <Icon>edit</Icon>
                    </IconButton>
                  </TableCell>
                  <TableCell>{row.TagDadosVariaveis}</TableCell>
                  <TableCell>{row.NomeDadosVariaveis}</TableCell>
                  <TableCell>{row.TamMaxDadosVariaveis}</TableCell>
                  <TableCell>{row.DescDadosVariaveis}</TableCell>
                </TableRow>
              ))}
            </TableBody>)}

            {totalCount === 0 && !isLoading && (
              <caption>{t('global.listaVazia')}</caption>
            )}

            <TableFooter>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={10}>
                    <LinearProgress variant='indeterminate' />
                  </TableCell>
                </TableRow>
              )}
              {(!isLoading) && (
                <TableRow>
                  <TableCell colSpan={2}>
                    {hasPermitionCreate && (<Button
                      color='primary'
                      disableElevation
                      variant='outlined'
                      onClick={() => navigate('/dadosvariaveis/detalhe/novo')}
                      startIcon={<Icon>add</Icon>}
                    >
                      <Typography variant='button' whiteSpace="nowrap" textOverflow="ellipsis" overflow="hidden">
                        {t('components.actions.novo')}
                      </Typography>
                    </Button>)}

                  </TableCell>
                  <TableCell colSpan={8}>
                    <TablePagination
                      component="div"
                      labelDisplayedRows={({ from, to, count }) => `${from} - ${to} ${t('global.of')} ${count !== -1 ? count : `${t('global.moreThan')} ${to}`}`}
                      labelRowsPerPage={t('global.rowsPerPage')}
                      showFirstButton={true}
                      showLastButton={true}
                      page={page}
                      count={totalCount}
                      rowsPerPage={rowsPerPage}
                      rowsPerPageOptions={[5,8,10,20,50,100]}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                    //onChange={(_, newPage) => setSearchParams({ busca, pagina: newPage.toString() }, { replace: true })}
                    />
                  </TableCell>
                </TableRow>
              )}
            </TableFooter>
          </Table>
        </TableContainer>

      </LayoutBaseDePagina>
      <AlertaSnackBar 
        open={stateSnack.open} 
        severity={stateSnack.severity as AlertColor} 
        message={stateSnack.message} 
        handleClose={handleCloseSnack} 
      />
    </>
  );
};
